// Store sync's API calls, all in one module (P5-D01…D04; docs/API.md «Store sync»), typed by the generated
// schema. The dry run's `preview` and a sync's `result` are dicts in the schema: their shapes (P5-D03 as
// built) are written here.
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';
import { qk } from '@/api/queries';

/** The organization's one store as Studio knows it (TZ §10); `theme_version` is the store's own counter. */
export type OrganizationStore = Schemas['StoreOut'];

export type SyncKind = 'provision' | 'theme' | 'catalog_dry_run' | 'catalog_import' | 'integration_status';

/** One call to the store backend: `status` running | succeeded | failed | skipped. */
export type StoreSync = Schemas['StoreSyncOut'];

/** «Следующий этап»: the handoff report (frozen), the provision job (or null) and the store. */
export type HandoffReport = Schemas['HandoffReportOut'];
export type HandoffOut = Schemas['HandoffOut'];

/** One row of «Catalog mapping»: an approved category of the latest snapshot. */
export type MappingRow = Schemas['MappingRow'];
export type MappingChange = Schemas['MappingChange'];

/** The dry run's preview (`SyncStoreStarted.preview`, a dict in the schema). */
export type ImportPreview = {
  categories: unknown[];
  products: { id: string; external_reference: string; name: string; slug: string; price: number | null; flags: string[] }[];
  skipped: { id: string; name?: string; reason: string }[];
  counts: { categories: number; products: number; skipped: number };
  flags: { price_unverified?: number; image_dropped?: number };
};
export type SyncStarted = Omit<Schemas['SyncStoreStarted'], 'preview'> & { preview: ImportPreview };
/** A finished import's job result. */
export type ImportResult = { created: number; updated: number; skipped: number; errors: { external_reference: string; code: string; message: string }[] };

/** The store backend's integrations (server truth). */
export type Integration = Schemas['IntegrationOut'];

export const orgStoreKey = (orgId: string) => ['store', orgId] as const;
export const storeSyncsKey = (siteId: string) => ['store', 'syncs', siteId] as const;
export const mappingKey = (orgId: string) => ['store', orgId, 'mapping'] as const;

/** GET /admin/organizations/{id}/store: the organization's store, or null («магазин не подключён»). */
export const orgStoreQuery = (orgId: string) =>
  queryOptions({
    queryKey: orgStoreKey(orgId),
    queryFn: async (): Promise<OrganizationStore | null> =>
      (await unwrap(api.GET('/admin/organizations/{organization_id}/store', { params: { path: { organization_id: orgId } } }))) ?? null,
    retry: false,
  });

/** GET /admin/websites/{id}/store-syncs: the sync log, newest first (polled while one runs). */
export const storeSyncsQuery = (siteId: string) =>
  queryOptions({
    queryKey: storeSyncsKey(siteId),
    queryFn: async (): Promise<StoreSync[]> =>
      (await unwrap(api.GET('/admin/websites/{website_id}/store-syncs', { params: { path: { website_id: siteId }, query: { size: 50 } } }))).items,
    refetchInterval: (q) => (q.state.data?.some((x) => x.status === 'running') ? 2000 : false),
    retry: false,
  });

/** GET /admin/organizations/{id}/catalog-mapping: one row per approved category, `create` by default. */
export const mappingQuery = (orgId: string) =>
  queryOptions({
    queryKey: mappingKey(orgId),
    queryFn: async (): Promise<MappingRow[]> =>
      (await unwrap(api.GET('/admin/organizations/{organization_id}/catalog-mapping', { params: { path: { organization_id: orgId } } }))).items,
    retry: false,
  });

/** GET /admin/organizations/{id}/store/integrations: the backend's integration states. */
export const integrationsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['store', orgId, 'integrations'] as const,
    queryFn: () => unwrap(api.GET('/admin/organizations/{organization_id}/store/integrations', { params: { path: { organization_id: orgId } } })),
    retry: false,
  });

/** PUT /admin/organizations/{id}/catalog-mapping: replaces the mapping (`update` needs `laravel_category_ref`). */
export async function saveMapping(orgId: string, items: MappingChange[]): Promise<MappingRow[]> {
  return (await unwrap(api.PUT('/admin/organizations/{organization_id}/catalog-mapping', { params: { path: { organization_id: orgId } }, body: { items } }))).items;
}

/**
 * POST /admin/websites/{id}/sync-store: a dry run first (the import modal's preview), then the import of the
 * same selection (`dry_run: false`; without a dry run it is 409 `dry_run_required`). Both answer the
 * `store.catalog_import` job.
 */
export async function syncStore(siteId: string, body: { dry_run: boolean; product_ids?: string[] }): Promise<SyncStarted> {
  const r = await unwrap(api.POST('/admin/websites/{website_id}/sync-store', { params: { path: { website_id: siteId } }, body }));
  return r as unknown as SyncStarted;
}

/** «Следующий этап»: POST /admin/websites/{id}/handoff → the report, the provision job (or null) and the store. */
export function handoff(siteId: string): Promise<HandoffOut> {
  return unwrap(api.POST('/admin/websites/{website_id}/handoff', { params: { path: { website_id: siteId } } }));
}

/** «PATCH тему в Laravel»: POST /admin/websites/{id}/theme-sync → the `store.theme_sync` job. */
export function syncTheme(siteId: string): Promise<Schemas['ThemeSyncStarted']> {
  return unwrap(api.POST('/admin/websites/{website_id}/theme-sync', { params: { path: { website_id: siteId } } }));
}

/** The latest sync of a kind. */
export const latestSync = (list: StoreSync[] | undefined, kind: SyncKind) => list?.find((x) => x.kind === kind) ?? null;

/** A finished import's counters from its sync row. */
export function importCounts(sync: StoreSync | null): ImportResult | null {
  if (!sync || sync.status !== 'succeeded') return null;
  const r = sync.result as Partial<ImportResult>;
  return { created: r.created ?? 0, updated: r.updated ?? 0, skipped: r.skipped ?? 0, errors: r.errors ?? [] };
}

/** After a store action: the store, the log, the website (its status) and the theme. */
export function refreshStore(qc: QueryClient, siteId: string, orgId: string): void {
  void qc.invalidateQueries({ queryKey: orgStoreKey(orgId) });
  void qc.invalidateQueries({ queryKey: storeSyncsKey(siteId) });
  void qc.invalidateQueries({ queryKey: qk.website(siteId) });
  void qc.invalidateQueries({ queryKey: ['websites', 'one', siteId, 'theme'] });
}
